import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { FeedbackPostDTO } from "../types/FeedbackPostDTO";
import { CreatePostDTO } from "../types/CreatePostDTO";
import {
  CreateNewPostAsync,
  GetAllFeedbackPosts,
  GetOpenFeedbackPosts,
  MarkFeedbackPostAsCompleteAsync,
  TogglePostLikeAsync,
} from "../services/FeedbackService";

export function useFeedback(showCompleted: boolean) {
  const { instance, account, user } = useAuth();

  const [feedbackPosts, setFeedbackPosts] = useState<FeedbackPostDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  const fetchPostsAsync = useCallback(async () => {
    setLoading(true);
    setFetchError(false);

    try {
      const posts = showCompleted
        ? await GetAllFeedbackPosts(instance, account)
        : await GetOpenFeedbackPosts(instance, account);
      setFeedbackPosts(posts);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [account, instance, showCompleted]);

  useEffect(() => {
    fetchPostsAsync();
  }, [fetchPostsAsync]);

  const submitPost = useCallback(
    async (postToCreate: CreatePostDTO) => {
      try {
        await CreateNewPostAsync(instance, account, postToCreate);
        await fetchPostsAsync();
      } catch (error) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    },
    [account, instance, fetchPostsAsync],
  );

  const likePost = useCallback(
    async (postId: number) => {
      if (!user?.id) return;
      setLoading(true);
      try {
        await TogglePostLikeAsync(instance, account, postId, user.id);
        await fetchPostsAsync();
      } catch (error) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    },
    [account, instance, fetchPostsAsync, user?.id],
  );

  const completePost = useCallback(
    async (postId: number) => {
      if (!user?.id) return;
      setLoading(true);
      try {
        await MarkFeedbackPostAsCompleteAsync(instance, account, postId, user.id);
        await fetchPostsAsync();
      } catch (error) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    },
    [account, instance, fetchPostsAsync, user?.id],
  );

  return {
    feedbackPosts,
    loading,
    fetchError,
    refreshPosts: fetchPostsAsync,
    submitPost,
    likePost,
    completePost,
  };
}
